import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Calendar, 
  FileText, 
  Pill, 
  SlidersHorizontal, 
  ShieldCheck, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  Sparkles,
  Clock
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import CtaSection from '../components/CtaSection';
import FaqSchema from '../components/FaqSchema';

function parseCmsSteps(rawHtml) {
  if (!rawHtml || typeof rawHtml !== 'string') return null;
  if (typeof window === 'undefined') return null;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
    let liElements = Array.from(doc.querySelectorAll('li'));
    if (liElements.length === 0) {
      liElements = Array.from(doc.querySelectorAll('p')).filter(p => p.textContent.trim().length > 0);
    }
    if (liElements.length === 0) return null;

    return liElements.map((el, index) => {
      const strongEl = el.querySelector('strong, b, h1, h2, h3, h4, h5, h6');
      let title = '';
      let rest = '';

      if (strongEl) {
        title = strongEl.textContent.trim();
        const clone = el.cloneNode(true);
        const cloneStrong = clone.querySelector('strong, b, h1, h2, h3, h4, h5, h6');
        if (cloneStrong) {
          if (cloneStrong.parentElement && (cloneStrong.parentElement.tagName === 'B' || cloneStrong.parentElement.tagName === 'STRONG')) {
            cloneStrong.parentElement.remove();
          } else {
            cloneStrong.remove();
          }
        }
        rest = clone.textContent.trim();
      } else {
        const fullText = el.textContent.trim();
        const parts = fullText.split(/ – | - |: /);
        if (parts.length > 1) {
          title = parts[0].trim();
          rest = parts.slice(1).join(' – ').trim();
        } else {
          title = fullText;
          rest = '';
        }
      }

      rest = rest.replace(/^[\s–—\-:;,]+/, '').trim();

      let meta = '';
      let desc = '';

      if (rest) {
        const subParts = rest.split(/ – | - /);
        const hasPriceOrTime = (str) => /\d+\s*(?:min|kr|sek|tim)/i.test(str);

        if (subParts.length > 1 && (hasPriceOrTime(subParts[0]) || hasPriceOrTime(subParts[1]))) {
          meta = `${subParts[0].trim()} – ${subParts[1].trim()}`;
          if (subParts.length > 2) {
            desc = subParts.slice(2).join(' – ').trim();
          }
        } else if (hasPriceOrTime(rest)) {
          meta = rest;
        } else {
          meta = '';
          desc = rest;
        }
      }

      const val = el.getAttribute('value');
      const stepNum = val ? (val.length === 1 ? `0${val}` : val) : String(index + 1).padStart(2, '0');

      return {
        num: stepNum,
        title: title || el.textContent.trim(),
        meta,
        desc
      };
    });
  } catch (err) {
    console.error('Error parsing CMS steps:', err);
    return null;
  }
}

export default function MedicinskBehandling() {
  const { lang, t, getLocalizedValue } = useLanguage();
  const [openFaq, setOpenFaq] = useState(0);
  const [cmsData, setCmsData] = useState(null);
  const [cmsFaqs, setCmsFaqs] = useState([]);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/medicinsk_behandling_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching medicinsk_behandling_side CMS data:', err);
      }
    };

    const fetchFaqs = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:medicinsk-behandling&limit=7&sort=sortering&order=asc');
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsFaqs(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching Medicinsk Behandling FAQs:', err);
      }
    };

    fetchCmsData();
    fetchFaqs();
  }, [lang]);

  const seoTitle = lang === 'da' ? 'Medicinsk behandling | Nordisk Psykiatri' : 'Medicinsk behandling | Nordisk Psykiatri';
  const seoDesc = lang === 'da' 
    ? 'Speciallægefaglig vurdering og medicinsk behandling med udgangspunkt i dig og din samlede situation – uanset om du søger behandling for ADHD eller andre psykiske problemstillinger.'
    : 'Specialistläkarbedömning och medicinsk behandling med utgångspunkt i dig och din helhetssituation – oavsett om du söker behandling för ADHD eller annan psykisk ohälsa.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  // Fallback and parsed steps for Pathway 1 & 2
  const defaultPathway1Steps = [
    {
      num: '01',
      title: lang === 'da' ? 'Indledende samtale hos psykolog' : 'Inledande samtal hos psykolog',
      meta: lang === 'da' ? '120 min. – 2.900 kr.' : '120 min. – 3.000 kr.',
      desc: ''
    },
    {
      num: '02',
      title: lang === 'da' ? 'Diagnostisk udredning' : 'Diagnostisk utredning',
      meta: '',
      desc: lang === 'da' ? 'Tilpasses problemstillingen' : 'Anpassas efter frågeställningen'
    },
    {
      num: '03',
      title: lang === 'da' ? 'Tilbagemelding og behandlingsplan' : 'Återkoppling och behandlingsplan',
      meta: '',
      desc: lang === 'da' 
        ? 'Sammen gennemgår vi resultaterne og lægger en plan for næste skridt.'
        : 'Tillsammans går vi igenom resultaten och lägger en plan för nästa steg.'
    },
    {
      num: '04',
      title: lang === 'da' ? 'Medicinsk vurdering hos psykiater' : 'Medicinsk bedömning hos psykiater',
      meta: lang === 'da' ? '60 min. – 2.500 kr.' : '60 min. – 2.400 kr.',
      desc: ''
    },
    {
      num: '05',
      title: lang === 'da' ? 'Medicinkontrol og opfølgning' : 'Medicinkontroll och uppföljning',
      meta: lang === 'da' ? '30 min. – 1.200 kr.' : '30 min. – 1.200 kr.',
      desc: ''
    }
  ];

  const defaultPathway2Steps = [
    {
      num: '01',
      title: lang === 'da' ? 'Indledende samtale hos psykolog' : 'Inledande samtal hos psykolog',
      meta: lang === 'da' ? '120 min. – 3.000 kr.' : '120 min. – 3.000 kr.',
      desc: lang === 'da'
        ? 'Vi gennemgår din tidligere udredning og vurderer grundlaget for en lægelig medicinsamtale.'
        : 'Vi går igenom din tidigare utredning och bedömer underlaget för ett läkarlett medicinsamtal.'
    },
    {
      num: '02',
      title: lang === 'da' ? 'Medicinsk vurdering hos psykiater' : 'Medicinsk bedömning hos psykiater',
      meta: lang === 'da' ? '60 min. – 2.400 kr.' : '60 min. – 2.400 kr.',
      desc: ''
    },
    {
      num: '03',
      title: lang === 'da' ? 'Medicinkontrol og opfølgning' : 'Medicinkontroll och uppföljning',
      meta: lang === 'da' ? '30 min. – 1.200 kr.' : '30 min. – 1.200 kr.',
      desc: ''
    }
  ];

  const rawVej1Trin = cmsData?.vej_1_trin ? getLocalizedValue(cmsData.vej_1_trin) : '';
  const parsedVej1 = parseCmsSteps(rawVej1Trin);
  const pathway1Steps = (parsedVej1 && parsedVej1.length > 0) ? parsedVej1 : defaultPathway1Steps;

  const rawVej2Trin = cmsData?.vej_2_trin ? getLocalizedValue(cmsData.vej_2_trin) : '';
  const parsedVej2 = parseCmsSteps(rawVej2Trin);
  const pathway2Steps = (parsedVej2 && parsedVej2.length > 0) ? parsedVej2 : defaultPathway2Steps;

  const faqs = lang === 'da' ? [
    {
      q: 'Skal jeg have en henvisning fra min egen læge?',
      a: 'Nej. Du kan booke en indledende samtale direkte hos os. Vi vurderer herefter, hvad næste relevante skridt er i dit forløb.'
    },
    {
      q: 'Kan jeg få medicinsk behandling, hvis jeg allerede har en diagnose?',
      a: 'Ja. Hvis du allerede er relevant udredt, gennemgår vi din tidligere udredning og vurderer, om der er et tilstrækkeligt grundlag for medicinsk behandling.'
    },
    {
      q: 'Hvem vurderer og ordinerer medicinen?',
      a: 'Den lægelige vurdering og ordination foretages af en psykiater. Behandlingen planlægges med fokus på din samlede situation og følges løbende.'
    },
    {
      q: 'Hvor ofte skal jeg til medicinkontrol?',
      a: 'Hyppigheden afhænger af behandlingen og din respons. I opstartsfasen kan der være behov for tættere opfølgning, mens intervallerne ofte bliver længere, når behandlingen er stabil.'
    },
    {
      q: 'Hvad sker der, hvis jeg oplever bivirkninger?',
      a: 'Bivirkninger indgår som en vigtig del af opfølgningen. Dosis eller behandling kan justeres, hvis det er nødvendigt og fagligt relevant.'
    },
    {
      q: 'Kan medicinsk behandling kombineres med psykoterapi?',
      a: 'Ja. For nogle giver det bedst mening at kombinere medicinsk behandling med psykoterapi. Det vurderes individuelt ud fra dine behov og mål.'
    },
    {
      q: 'Er der mulighed for medicinsk behandling for andre tilstande end ADHD?',
      a: 'Ja, vores speciallæger i psykiatri kan vurdere og iværksætte medicinsk behandling ved en lang række psykiatriske problemstillinger, herunder depression, angst, OCD og søvnproblemer.'
    }
  ] : [
    {
      q: 'Måste jag ha en remiss från min vårdcentral?',
      a: 'Nej. Du kan boka ett inledande samtal direkt hos oss. Vi bedömer därefter vad som är nästa relevanta steg i din process.'
    },
    {
      q: 'Kan jag få medicinsk behandling om jag redan har en diagnos?',
      a: 'Ja. Om du redan är relevant utredd går vi igenom din tidigare utredning och bedömer om det finns ett tillräckligt underlag för medicinsk behandling.'
    },
    {
      q: 'Vem bedömer och ordinerar medicinen?',
      a: 'Den medicinska bedömningen och förskrivningen görs av en specialistläkare i psykiatri. Behandlingen planeras med fokus på din helhetssituation och följs upp löpande.'
    },
    {
      q: 'Hur ofta behöver jag komma på medicinkontroll?',
      a: 'Frekvensen beror på behandlingen och ditt svar på medicineringen. Under uppstartsfasen kan tätare uppföljning behövas, medan intervallen oftast blir längre när behandlingen är stabil.'
    },
    {
      q: 'Vad händer om jag upplever biverkningar?',
      a: 'Biverkningar är en viktig del av uppföljningen. Dos eller preparatval kan justeras vid behov och medicinsk relevans.'
    },
    {
      q: 'Kan medicinsk behandling kombineras med psykoterapi?',
      a: 'Ja. För många ger en kombination av medicinsk behandling och psykoterapi bäst resultat. Detta bedöms individuellt utifrån dina behov och mål.'
    }
  ];

  const processCards = lang === 'da' ? [
    {
      title: 'Opstart og medicinvalg',
      desc: 'Psykiateren vurderer, hvilken behandling der er relevant, og lægger en plan for opstart.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M35 58V26M35 35C27 17 14 14 13 16c-2 13 8 21 22 19M35 40c8-18 21-21 22-19 2 13-8 21-22 19" />
        </svg>
      )
    },
    {
      title: 'Justering af dosis',
      desc: 'Dosis tilpasses gradvist med fokus på den bedst mulige virkning og færrest mulige bivirkninger.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M14 54h10V42H14zM31 54h10V31H31zM48 54h10V20H48zM16 32l12-11 8 5 18-16M46 10h8v8" />
        </svg>
      )
    },
    {
      title: 'Effekt og bivirkninger',
      desc: 'Vi følger systematisk, hvordan behandlingen virker, og om der opstår gener eller bivirkninger.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M35 10c11 7 18 8 24 9v16c0 15-9 22-24 28C20 57 11 50 11 35V19c6-1 13-2 24-9zM26 36l6 6 13-14" />
        </svg>
      )
    },
    {
      title: 'Løbende opfølgning',
      desc: 'Behandlingen evalueres og justeres ved behov. Psykoterapi kan inddrages, når det er relevant.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M56 31c-2-11-11-19-23-19-9 0-16 5-20 12M14 17v9h9M14 39c2 11 11 19 23 19 9 0 16-5 20-12M56 53v-9h-9" />
        </svg>
      )
    }
  ] : [
    {
      title: 'Uppstart och läkemedelsval',
      desc: 'Psykiatrikern bedömer vilken behandling som är lämplig och lägger upp en plan för uppstart.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M35 58V26M35 35C27 17 14 14 13 16c-2 13 8 21 22 19M35 40c8-18 21-21 22-19 2 13-8 21-22 19" />
        </svg>
      )
    },
    {
      title: 'Dosjustering',
      desc: 'Dosen anpassas stegvis med fokus på bästa möjliga effekt och minsta möjliga biverkningar.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M14 54h10V42H14zM31 54h10V31H31zM48 54h10V20H48zM16 32l12-11 8 5 18-16M46 10h8v8" />
        </svg>
      )
    },
    {
      title: 'Effekt och biverkningar',
      desc: 'Vi följer systematiskt hur behandlingen fungerar och om det uppstår besvär eller biverkningar.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M35 10c11 7 18 8 24 9v16c0 15-9 22-24 28C20 57 11 50 11 35V19c6-1 13-2 24-9zM26 36l6 6 13-14" />
        </svg>
      )
    },
    {
      title: 'Löpande uppföljning',
      desc: 'Behandlingen utvärderas och anpassas vid behov. Psykoterapi kan integreras när det är relevant.',
      icon: (
        <svg viewBox="0 0 70 70" className="w-12 h-12 stroke-petrol-dark fill-none stroke-[1.8] stroke-linecap-round stroke-linejoin-round">
          <path d="M56 31c-2-11-11-19-23-19-9 0-16 5-20 12M14 17v9h9M14 39c2 11 11 19 23 19 9 0 16-5 20-12M56 53v-9h-9" />
        </svg>
      )
    }
  ];

  return (
    <div className="bg-sand-light min-h-screen text-petrol-dark selection:bg-[#D1E1BE] selection:text-petrol-dark">
      
      {/* 1. HERO SECTION */}
      <FluidSubpageHero
        eyebrow={cmsData?.hero_badge ? getLocalizedValue(cmsData.hero_badge) : (lang === 'da' ? 'PSYKIATER' : 'BEHANDLING')}
        title={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : (lang === 'da' ? 'Medicinsk behandling hos psykiater' : 'Medicinsk behandling')}
        description={cmsData?.hero_beskrivelse ? getLocalizedValue(cmsData.hero_beskrivelse) : (lang === 'da'
          ? 'Vores psykiater tilbyder grundig vurdering og individuelt tilrettelagt medicinsk behandling af ADHD og andre psykiske lidelser. Behandlingen tager udgangspunkt i dine symptomer, behov og samlede livssituation og følges tæt med fokus på effekt, bivirkninger og tryghed.'
          : 'Specialistläkarbedömning och behandling med utgångspunkt i dig och din helhetssituation – oavsett om du söker behandling för ADHD eller annan psykisk ohälsa.')}
        ctaText={cmsData?.hero_cta_tekst ? getLocalizedValue(cmsData.hero_cta_tekst) : (lang === 'da' ? 'Kontakt os om booking' : 'Kontakta oss om bokning')}
        ctaLink={cmsData?.hero_cta_link || "/kontakt"}
        secondaryCtaText={lang === 'da' ? 'Se priser' : 'See prices'}
        secondaryCtaLink="/priser"
        bgImageUrl={cmsData?.hero_billede?.[0] || "https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/nord-medicinsk-behandling.png"}
        bgImageAlt={lang === 'da' ? 'Medicinsk behandling hos Nordisk Psykiatri' : 'Medicinsk behandling hos Nordisk Psykiatri'}
        bgImageClass="w-full h-full object-cover object-right"
        showBgOverlay={false}
        mobileShowBgBelow={true}
        mobileBgImageUrl={(cmsData && cmsData.hero_billede_mobil && cmsData.hero_billede_mobil[0])
          ? (cmsData.hero_billede_mobil[0].includes('/cdn-cgi/image/') ? cmsData.hero_billede_mobil[0] : `https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${cmsData.hero_billede_mobil[0].replace('https://assets.auxodev.dk/', '')}`)
          : "https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/nord-medicinsk-behandling.png"}
        mobileBgImageAlt={lang === 'da' ? 'Medicinsk behandling hos Nordisk Psykiatri' : 'Medicinsk behandling hos Nordisk Psykiatri'}
        className="bg-[#CFE0DB]"
        style={{ backgroundColor: '#CFE0DB' }}
        descriptionClass="max-w-xl"
        reviewStamp={cmsData?.fagligt_gennemgaaet ? getLocalizedValue(cmsData.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Martin Bergholtz, læge i psykiatri | Opdateret september 2026' : 'Clinically reviewed by Martin Bergholtz, Medical Doctor in Psychiatry | Updated September 2026')}
      />

      {/* 2. TO VEJE TIL MEDICINSK BEHANDLING */}
      <section id="veje" className="py-16 sm:py-24 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 space-y-12">
          
            {/* Main Pathways Box - Redesigned to match the Editorial Nordic Minimalist comparison timeline specification */}
            <div className="bg-[#fbfaf6] border border-[#cbd7cc]/60 rounded-3xl p-6 sm:p-10 lg:p-14 shadow-xs">
              <div className="max-w-3xl mb-12 lg:mb-16">
                <span className="font-display italic text-[#5a7b5c] text-lg sm:text-xl block mb-2">
                  {lang === 'da' ? 'Forløb & proces' : 'Process & pathways'}
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1c302a] font-normal tracking-tight mb-3">
                  {cmsData?.veje_overskrift ? getLocalizedValue(cmsData.veje_overskrift) : (lang === 'da' ? 'To veje til medicinsk behandling' : 'Two routes to medical treatment')}
                </h2>
                <p className="text-[#556c64] text-sm sm:text-base leading-relaxed">
                  {cmsData?.veje_undertekst ? getLocalizedValue(cmsData.veje_undertekst) : (lang === 'da' 
                    ? 'Uanset om du lige har fået mistanke om en diagnose eller allerede er udredt, hjælper vi dig videre på en tryg og overskuelig måde.'
                    : 'Whether you have only just begun to suspect a diagnosis or have already been assessed, we help you move forward in a safe and clear way.')}
                </p>
              </div>

              {/* Grid 2 Pathways / Comparison Columns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[#cdd9cb]/60 gap-12 lg:gap-0">
                
                {/* PATHWAY 1: UDEN DIAGNOSE */}
                <article className="flex flex-col justify-between lg:pr-12 xl:pr-16 pt-0">
                  <div>
                    {/* Header */}
                    <header className="mb-10">
                      <span className="font-display italic text-lg sm:text-xl text-[#5a7b5c] block mb-1">
                        {cmsData?.vej_1_badge ? getLocalizedValue(cmsData.vej_1_badge) : (lang === 'da' ? 'Uden diagnose' : 'Without a diagnosis')}
                      </span>
                      <h3 className="font-display text-3xl sm:text-4xl font-normal text-[#1c302a] tracking-tight mb-2">
                        {cmsData?.vej_1_overskrift ? getLocalizedValue(cmsData.vej_1_overskrift) : (lang === 'da' ? 'Jeg skal udredes først' : 'I need to be assessed first')}
                      </h3>
                      <p className="text-sm text-[#556c64] leading-relaxed">
                        {cmsData?.vej_1_undertekst ? getLocalizedValue(cmsData.vej_1_undertekst) : (lang === 'da' ? 'Hvis du endnu ikke har en tilstrækkelig udredning.' : 'If you do not yet have a sufficient assessment.')}
                      </p>
                    </header>

                    {/* Timeline Rail */}
                    <div className="relative pl-7 sm:pl-8 space-y-9 sm:space-y-10 before:absolute before:left-[5px] before:top-2.5 before:bottom-3 before:w-px before:bg-[#cbd7cc]">
                      {pathway1Steps.map((step, idx) => {
                        const isFirst = idx === 0;
                        const metaParts = step.meta ? step.meta.split(/–|-/).map(s => s.trim()) : [];
                        const duration = metaParts[0] || '';
                        const price = metaParts.length > 1 ? metaParts.slice(1).join(' – ') : '';

                        return (
                          <div key={idx} className="relative">
                            {/* Step Node Indicator */}
                            {isFirst ? (
                              <span className="absolute -left-7 sm:-left-8 top-1.5 w-3 h-3 rounded-full bg-[#1c302a] ring-4 ring-[#cdd9cb]/60" />
                            ) : (
                              <span className="absolute -left-7 sm:-left-8 top-1.5 w-3 h-3 rounded-full bg-[#fbfaf6] border border-[#8ba888]" />
                            )}

                            {/* Step Tag */}
                            <div className="text-[11px] font-bold tracking-widest text-[#5a7b5c] uppercase mb-1">
                              {lang === 'da' ? `Trin ${step.num || String(idx + 1).padStart(2, '0')}` : `Step ${step.num || String(idx + 1).padStart(2, '0')}`}
                            </div>

                            {/* Step Title */}
                            <h4 className="font-display text-xl sm:text-[22px] font-normal text-[#1c302a] leading-snug mb-1.5">
                              {step.title}
                            </h4>

                            {/* Metadata / Duration & Price */}
                            {step.meta && (
                              <div className="flex items-center gap-3 text-xs sm:text-sm text-[#556c64] mb-1">
                                {duration && <span>{duration}</span>}
                                {price && (
                                  <>
                                    <span className="w-5 h-px bg-[#cdd9cb]" />
                                    <span className="font-bold text-[#1c302a]">{price}</span>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Description */}
                            {step.desc && (
                              <p className="text-xs sm:text-sm text-[#556c64] leading-relaxed mt-1">
                                {step.desc}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Primary Action Button (Pill CTA) */}
                  <div className="pt-10 sm:pt-12">
                    <Link
                      to={cmsData?.vej_1_knap_link || "/udredning"}
                      className="group inline-flex items-center justify-between sm:justify-center gap-3 w-full sm:w-auto px-7 py-4 rounded-full bg-[#1c302a] hover:bg-[#264b44] text-white text-sm font-medium transition-all duration-200 shadow-sm"
                    >
                      <span>{cmsData?.vej_1_knap_tekst ? getLocalizedValue(cmsData.vej_1_knap_tekst) : (lang === 'da' ? 'Start med psykiatrisk udredning' : 'Start with a psychiatric assessment')}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>

                {/* PATHWAY 2: MED DIAGNOSE */}
                <article className="flex flex-col justify-between lg:pl-12 xl:pl-16 pt-10 lg:pt-0">
                  <div>
                    {/* Header */}
                    <header className="mb-10">
                      <span className="font-display italic text-lg sm:text-xl text-[#5a7b5c] block mb-1">
                        {cmsData?.vej_2_badge ? getLocalizedValue(cmsData.vej_2_badge) : (lang === 'da' ? 'Med diagnose' : 'With a diagnosis')}
                      </span>
                      <h3 className="font-display text-3xl sm:text-4xl font-normal text-[#1c302a] tracking-tight mb-2">
                        {cmsData?.vej_2_overskrift ? getLocalizedValue(cmsData.vej_2_overskrift) : (lang === 'da' ? 'Jeg har allerede en diagnose' : 'I already have a diagnosis')}
                      </h3>
                      <p className="text-sm text-[#556c64] leading-relaxed">
                        {cmsData?.vej_2_undertekst ? getLocalizedValue(cmsData.vej_2_undertekst) : (lang === 'da' ? 'Hvis du er relevant udredt og ønsker medicinsk behandling.' : 'If you have been appropriately assessed and would like medical treatment.')}
                      </p>
                    </header>

                    {/* Timeline Rail */}
                    <div className="relative pl-7 sm:pl-8 space-y-9 sm:space-y-10 before:absolute before:left-[5px] before:top-2.5 before:bottom-3 before:w-px before:bg-[#cbd7cc]">
                      {pathway2Steps.map((step, idx) => {
                        const isFirst = idx === 0;
                        const metaParts = step.meta ? step.meta.split(/–|-/).map(s => s.trim()) : [];
                        const duration = metaParts[0] || '';
                        const price = metaParts.length > 1 ? metaParts.slice(1).join(' – ') : '';

                        return (
                          <div key={idx} className="relative">
                            {/* Step Node Indicator */}
                            {isFirst ? (
                              <span className="absolute -left-7 sm:-left-8 top-1.5 w-3 h-3 rounded-full bg-[#1c302a] ring-4 ring-[#cdd9cb]/60" />
                            ) : (
                              <span className="absolute -left-7 sm:-left-8 top-1.5 w-3 h-3 rounded-full bg-[#fbfaf6] border border-[#8ba888]" />
                            )}

                            {/* Step Tag */}
                            <div className="text-[11px] font-bold tracking-widest text-[#5a7b5c] uppercase mb-1">
                              {lang === 'da' ? `Trin ${step.num || String(idx + 1).padStart(2, '0')}` : `Step ${step.num || String(idx + 1).padStart(2, '0')}`}
                            </div>

                            {/* Step Title */}
                            <h4 className="font-display text-xl sm:text-[22px] font-normal text-[#1c302a] leading-snug mb-1.5">
                              {step.title}
                            </h4>

                            {/* Metadata / Duration & Price */}
                            {step.meta && (
                              <div className="flex items-center gap-3 text-xs sm:text-sm text-[#556c64] mb-1">
                                {duration && <span>{duration}</span>}
                                {price && (
                                  <>
                                    <span className="w-5 h-px bg-[#cdd9cb]" />
                                    <span className="font-bold text-[#1c302a]">{price}</span>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Description */}
                            {step.desc && (
                              <p className="text-xs sm:text-sm text-[#556c64] leading-relaxed mt-1">
                                {step.desc}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Primary Action Button (Pill CTA) */}
                  <div className="pt-10 sm:pt-12">
                    <Link
                      to={cmsData?.vej_2_knap_link || "/kontakt"}
                      className="group inline-flex items-center justify-between sm:justify-center gap-3 w-full sm:w-auto px-7 py-4 rounded-full bg-[#1c302a] hover:bg-[#264b44] text-white text-sm font-medium transition-all duration-200 shadow-sm"
                    >
                      <span>{cmsData?.vej_2_knap_tekst ? getLocalizedValue(cmsData.vej_2_knap_tekst) : (lang === 'da' ? 'Kom i gang med medicinsk behandling' : 'Get started with medical treatment')}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>

              </div>
            </div>

            {/* Booking Banner Strip */}
            <div className="bg-[#1e3a35] text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs border border-[#162d29]">
              <div className="flex items-center gap-4 sm:gap-5">
                <div className="w-12 sm:w-14 h-12 sm:h-14 rounded-full bg-white/10 flex items-center justify-center shrink-0 border border-white/20 shadow-2xs">
                  <Calendar className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
                </div>
                <div>
                  <strong className="font-display text-lg sm:text-xl font-medium block leading-snug text-white">
                    {cmsData?.booking_strip_overskrift ? getLocalizedValue(cmsData.booking_strip_overskrift) : (lang === 'da' 
                      ? 'Du kan booke direkte online uden henvisning fra egen læge'
                      : 'Du kan boka direkt online utan remiss från vårdcentral')}
                  </strong>
                  <span className="text-xs sm:text-sm text-white/80 block mt-0.5">
                    {cmsData?.booking_strip_tekst ? getLocalizedValue(cmsData.booking_strip_tekst) : (lang === 'da' 
                      ? 'Vi har typisk kort ventetid og tager os god tid til hver eneste samtale.'
                      : 'Vi har oftast kort väntetid och tar oss god tid för varje enskilt samtal.')}
                  </span>
                </div>
              </div>

              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full bg-white text-petrol-dark text-xs sm:text-sm font-semibold hover:bg-sand-light transition-all duration-200 shrink-0 shadow-xs group w-full sm:w-auto justify-center"
              >
                <span>{cmsData?.booking_strip_knap_tekst ? getLocalizedValue(cmsData.booking_strip_knap_tekst) : (lang === 'da' ? 'Se ledige tider' : 'Se lediga tider')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

        </div>
      </section>

      {/* 3. ET BEHANDLINGSFORLØB MED LØBENDE OPFØLGNING */}
      <section className="py-16 sm:py-24 bg-[#D1E1BE] border-t border-b border-[#c2ceaf]">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          
          <div className="max-w-3xl mb-12">
            <h2 className="font-display text-3xl sm:text-4xl text-petrol-dark font-normal leading-tight mb-4">
              {cmsData?.forloeb_overskrift ? getLocalizedValue(cmsData.forloeb_overskrift) : (lang === 'da' 
                ? 'Et behandlingsforløb med løbende opfølgning'
                : 'Ett behandlingsförlopp med löpande uppföljning')}
            </h2>
            <div className="w-12 h-0.5 bg-[#D1E1BE] mb-4" />
            <p className="text-text-muted text-sm sm:text-base leading-relaxed">
              {cmsData?.forloeb_undertekst ? getLocalizedValue(cmsData.forloeb_undertekst) : (lang === 'da'
                ? 'Medicinsk behandling tilrettelægges individuelt og justeres løbende ud fra effekt, bivirkninger og din samlede situation.'
                : 'Medicinsk behandling anpassas individuellt och justeras kontinuerligt utifrån effekt, biverkningar och din totala livssituation.')}
            </p>
          </div>

          {/* 4 Process Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {processCards.map((card, idx) => (
              <div 
                key={idx}
                className="bg-white/65 backdrop-blur-xs border border-white/60 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6 shadow-xs hover:bg-white/80 hover:border-white transition-all"
              >
                <div className="w-20 h-20 rounded-full border border-[#b8cba3] bg-white/70 flex items-center justify-center shrink-0 shadow-xs">
                  {card.icon}
                </div>
                <div>
                  <h3 className="font-display text-xl sm:text-2xl text-petrol-dark font-normal mb-2">
                    {idx === 0 && cmsData?.forloeb_kort_1_titel ? getLocalizedValue(cmsData.forloeb_kort_1_titel) :
                     idx === 1 && cmsData?.forloeb_kort_2_titel ? getLocalizedValue(cmsData.forloeb_kort_2_titel) :
                     idx === 2 && cmsData?.forloeb_kort_3_titel ? getLocalizedValue(cmsData.forloeb_kort_3_titel) :
                     idx === 3 && cmsData?.forloeb_kort_4_titel ? getLocalizedValue(cmsData.forloeb_kort_4_titel) : card.title}
                  </h3>
                  <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">
                    {idx === 0 && cmsData?.forloeb_kort_1_tekst ? getLocalizedValue(cmsData.forloeb_kort_1_tekst) :
                     idx === 1 && cmsData?.forloeb_kort_2_tekst ? getLocalizedValue(cmsData.forloeb_kort_2_tekst) :
                     idx === 2 && cmsData?.forloeb_kort_3_tekst ? getLocalizedValue(cmsData.forloeb_kort_3_tekst) :
                     idx === 3 && cmsData?.forloeb_kort_4_tekst ? getLocalizedValue(cmsData.forloeb_kort_4_tekst) : card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 4. OFTE STILLEDE SPØRGSMÅL (FAQ) */}
      <section id="faq" className="pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-24 bg-sand-light">
        <FaqSchema 
          items={(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
            q: getLocalizedValue(f.spoergsmaal),
            a: getLocalizedValue(f.svar)
          })) : faqs).slice(0, 7)} 
        />
        <div className="mx-auto max-w-[900px] px-6">
          <div className="bg-[#faf8f4] rounded-lg p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-petrol-dark mb-6">
              {cmsData?.faq_overskrift ? getLocalizedValue(cmsData.faq_overskrift) : (lang === 'da' ? 'Ofte stillede spørgsmål' : 'Vanliga frågor')}
            </h2>

            <div className="space-y-3">
              {(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
                q: getLocalizedValue(f.spoergsmaal),
                a: getLocalizedValue(f.svar)
              })) : faqs).slice(0, 7).map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div 
                    key={idx} 
                    className={`border-t border-[#d8d5ce] last:border-b border-[#d8d5ce] transition-all duration-200 ${
                      isOpen ? 'py-3' : 'py-1'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                      className={`flex w-full items-center justify-between text-left font-sans text-sm font-bold text-petrol-dark focus:outline-none transition-all duration-200 cursor-pointer ${
                        isOpen ? 'py-2' : 'py-1.5'
                      }`}
                    >
                      <span>{item.q}</span>
                      {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-petrol-medium shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-petrol-medium shrink-0" />
                      )}
                    </button>
                    {/* Bevares altid i DOM for Google og crawlere */}
                    <div 
                      className={`text-xs sm:text-sm text-[#586067] leading-relaxed ${
                        isOpen ? 'mt-2 pb-2 block' : 'hidden'
                      }`}
                      dangerouslySetInnerHTML={typeof item.a === 'string' && item.a.includes('<') ? { __html: item.a } : undefined}
                    >
                      {typeof item.a === 'string' && item.a.includes('<') ? null : item.a}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 5. LÆS MERE OM BEHANDLING OG UDREDNING */}
      <section className="bg-white px-6 sm:px-8 py-16 lg:py-20 border-t border-sand-medium/60">
        <div className="max-w-[900px] mx-auto text-center">
          {/* Header */}
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-normal tracking-tight text-petrol-dark leading-tight">
            {lang === 'da' ? 'Læs mere om behandling og udredning' : 'Read more about treatment and assessment'}
          </h2>

          {/* Subtitle */}
          <p className="mt-3 font-sans text-[15px] sm:text-base md:text-lg text-text-muted max-w-none mx-auto font-normal leading-relaxed md:whitespace-nowrap">
            {lang === 'da'
              ? 'Find mere information om medicinsk behandling, udredning og priser hos Nordisk Psykiatri.'
              : 'Find more information about medical treatment, assessment, and prices at Nordisk Psykiatri.'}
          </p>

          {/* Pill Button Bar */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <Link
              to="/adhd"
              className="px-7 py-3 rounded-full border border-petrol-dark/40 text-petrol-dark text-[15px] font-medium transition-all duration-200 hover:border-petrol-dark hover:bg-petrol-dark/5 focus:outline-none focus:ring-2 focus:ring-petrol-dark/20 inline-flex items-center justify-center cursor-pointer"
            >
              {lang === 'da' ? 'ADHD-udredning' : 'ADHD assessment'}
            </Link>
            <Link
              to="/autisme"
              className="px-7 py-3 rounded-full border border-petrol-dark/40 text-petrol-dark text-[15px] font-medium transition-all duration-200 hover:border-petrol-dark hover:bg-petrol-dark/5 focus:outline-none focus:ring-2 focus:ring-petrol-dark/20 inline-flex items-center justify-center cursor-pointer"
            >
              {lang === 'da' ? 'Autismeudredning' : 'Autism assessment'}
            </Link>
            <Link
              to="/priser"
              className="px-7 py-3 rounded-full border border-petrol-dark/40 text-petrol-dark text-[15px] font-medium transition-all duration-200 hover:border-petrol-dark hover:bg-petrol-dark/5 focus:outline-none focus:ring-2 focus:ring-petrol-dark/20 inline-flex items-center justify-center cursor-pointer"
            >
              {lang === 'da' ? 'Priser' : 'Prices'}
            </Link>
            <a
              href="#faq"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-7 py-3 rounded-full border border-petrol-dark/40 text-petrol-dark text-[15px] font-medium transition-all duration-200 hover:border-petrol-dark hover:bg-petrol-dark/5 focus:outline-none focus:ring-2 focus:ring-petrol-dark/20 inline-flex items-center justify-center cursor-pointer"
            >
              {lang === 'da' ? 'Ofte stillede spørgsmål' : 'Frequently asked questions'}
            </a>
          </div>
        </div>
      </section>

      {/* 6. CTA SECTION */}
      <CtaSection />

    </div>
  );
}
